import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, AfterViewChecked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InfoService } from '@app/core/info/info.service';
import { ISigningTextsInterface, SigningTexts, ISigningValuesInterface } from '@app/certificates/signing/signing';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { SigningService } from '@app/certificates/signing/signing.service';
import { takeUntil, first } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ISpringResponseBody } from '@app/shared/interfaces/response-interfaces';
import { ICertificateData, CertStatus, PackType } from '@app/certificates/manage/manage';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { SigningHistoryRequest, AdditionalParams, ContentType } from '@app/certificates/history/history';
import { HistoryService } from '@app/certificates/history/history.service';
import { selectorCertificate, ActionCertificatesGet } from '@app/certificates/manage/manage.reducer';
import { cadesValues } from '@app/certificates/signing/signing.values';
import { pdfValues } from '@app/certificates/signing/signing.values';
import { xmlValues } from '@app/certificates/signing/signing.values';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { EmergencyService } from '@app/core/auth/component/emergency-login/emergency-login.service';
import { ProfileRemainingCountComponent } from '@app/profile/profile-remaining-count/profile-remaining-count.component';
import { ActionSigningSuccess } from '@app/certificates/signing/signing.actions';
import { DocumentStatus } from '@app/certificates/history/signing-history/signing-history.component';
import { ActionDownload } from '../download.action';
import { ProfileService } from '@app/core/services/profile/profile.service';
import { SearchUserIdentifiers, SearchUserRequestParams } from '@app/profile/settings/settings';
import { selectorAuth, SessionStorageService } from '@app/core';
import { IUserData } from '@app/profile/main/details';
import { ActionProfilePlanGet } from '@app/profile/profile-remaining-count/profile-plan.actions';
import { AuthenticationService } from '@app/core/auth/authentication.service';
import { stat } from 'fs';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'cloud-signing',
  templateUrl: './signing.component.html',
  styleUrls: ['./signing.component-theme.scss', './signing.component.scss']
})
export class SigningComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild(ProfileRemainingCountComponent) remainingCountComponent: ProfileRemainingCountComponent;
  readonly dateTimeFormat = environment.dateTimeFormat;
  readonly expirationTime = environment.signedDocExpirationTime;

  fileForm: FormGroup;
  signTypeForm: FormGroup;
  texts: ISigningTextsInterface = new SigningTexts();
  values: ISigningValuesInterface;
  unsubscribe$: Subject<void> = new Subject<void>();
  selectedCert: ICertificateData;
  file: File;
  disabled = false;
  loading: boolean;
  isEditable = true;
  isSignSuccessfully: boolean;
  signedfileName: string;
  private _signDocReqId: string;
  private i18nTexts = 'signingTexts';
  isCertInvalid: boolean;
  notDownloadedDocuments = false;
  validCertificates = true;
  downloads = [];
  certs: ICertificateData[] = new Array();
  padesTypes = pdfValues.signType;
  cadesTypes = cadesValues.signType;
  xadesTypes = xmlValues.signType;
  loadingComponents = true;
  signingCancelled: boolean;
  signingStepCompleted = false;
  otherSigner = false;
  userIdentifier: string;
  searchOptions: SearchUserRequestParams;
  otherSignerCertReqId = '';
  userData: IUserData;
  sendSuccess: boolean;
  users = [];

  periodsDictionary = [
    { key: '1', unit: 'hour', value: 60 },
    { key: '2', unit: 'hour', value: 120 },
    { key: '4', unit: 'hour', value: 240 },
    { key: '8', unit: 'hour', value: 480 },
    { key: '1', unit: 'day', value: 1440 },
    { key: '2', unit: 'day', value: 2880 },
    { key: '3', unit: 'day', value: 4320 },
    { key: '4', unit: 'day', value: 5760 },
    { key: '5', unit: 'day', value: 7200 },
    { key: '6', unit: 'day', value: 8640 },
    { key: '7', unit: 'day', value: 10080 }
  ];

  validity = 10080;
  isLoggedInOnlyQES: boolean;

  constructor(
    public info: InfoService,
    private signingService: SigningService,
    private translateService: TranslateService,
    private historyService: HistoryService,
    private formBuilder: FormBuilder,
    private cd: ChangeDetectorRef,
    private store: Store<any>,
    private router: Router,
    private emergency: EmergencyService,
    private profileService: ProfileService,
    public sessionStorageService: SessionStorageService,
    public auth: AuthenticationService,
  ) {
    if (this.emergency.getEmergencyState()) {
      this.router.navigate(['/manage/manage']);
    }

    this.loadTranslations();

    this.store
      .select(selectorAuth)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.userData) { this.userData = res.userData; }
        if (this.userData == undefined) {
          this.userData = this.sessionStorageService.getItem("loginEventData")
        }
      });
    this.auth.isLoggedInOnlyQES$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(status => {
        this.isCertInvalid = status
      });
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((certs: any) => {
        // In case certificates are not loaded yet sent get request
        if (!certs || certs.length === 0) {
          this.store.dispatch(new ActionCertificatesGet());
          return;
        }
        // Certificates are loaded
        this.certs = certs;
        if (this.certs.length && !this.certs.find(s => s.status === CertStatus.VALID)) {
          this.validCertificates = false;
        }
        // Check for signed but not yet downloaded documents
        // after check finished loadingComponents is made to false
        this.onCertSelected(certs)
        this.checkForDownloads();

      });
    this.store.dispatch(new ActionProfilePlanGet());
  }

  ngOnInit() {
    this.loadingComponents = false;
    this.signingCancelled = false;
    this.info.dismissAll();
    this.initFileForm();
  }

  ngAfterViewChecked() {
    this.cd.detectChanges();
  }

  onSubmit(): void {
    this.isEditable = false;
    this.loading = true;
    const formData = new FormData();
    const formValues = this.signTypeForm.value;
    formValues.toBeArchived = !formValues.notToBeArchived;
    delete formValues.notToBeArchived;
    this.signTypeForm.updateValueAndValidity();

    formData.append('file', this.file, this.file.name);
    formData.append('filename', this.file.name);

    Object.keys(formValues).map(k => formData.append(k, formValues[k]));
    const requestedSequences = this.setSigners();
    formData.append('requestedSequences', JSON.stringify(requestedSequences));
    formData.append('cmrId', this.userData.cmrId)
    formData.append('validity', this.validity.toString());


    if (this.isSigningValid()) {
      this.signingStepCompleted = true;
    }

    // send user input params and wait for response with signDocReqId
    this.signingService.sendForSigning(formData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: ISpringResponseBody) => {
        if (result.status === 200) {
          this._signDocReqId = result.data.signDocReqId;
          if (!this.otherSigner) {
            this.handleFileSingingResponse();
            setTimeout(() => {
              this.info.endWait();
              this.file = null;
              this.fileForm.reset();
              this.stepper.previous();
            }, 5000);
            this.ngOnDestroy();
            this.info.success('signingTexts.signingSendSuccess');
            this.isEditable = true;
          } else {
            this.sendSuccess = true;
            this.loading = false;
            this.info.success('signingTexts.signingSendSuccess');
          }
          this.isEditable = true;
        } else {
          this.loading = false;
          this.isSignSuccessfully = false;
          this.isEditable = true;
        }
        this.store.dispatch(new ActionProfilePlanGet());
      });
  }

  setSigners(): any[] {
    // TO DO: implement setting of actual time to sign
    const sequences = [];
    let selfAdded = false;
    if (this.otherSigner) {
      for (let i = 0; i < this.users.length; i++) {
        sequences.push({
          'cmrId': this.users[i].cmrId,
          'sequenceIndex': (i + 1) + '',
          'timeToSign': this.validity
        });
      }
    } else {
      sequences.push({
        'cmrId': this.userData.cmrId,
        'sequenceIndex': (1) + '',
        'timeToSign': this.validity
      });
    }
    return sequences;
  }
  // send signDocReqId to service - getSignDocStatus with get request for created url
  handleFileSingingResponse() {
    this.signingService.pullUntilSigning(this._signDocReqId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result: ISpringResponseBody) => {
          if (result.status === 200) {
            this.loading = false;
            this.isSignSuccessfully = true;
            this._signDocReqId = result.data.ID;
            this.store.dispatch(new ActionSigningSuccess());
            this.info.success('signingTexts.signingSuccess');
          } else {
            if (result.status === 410) {
              this.info.error('signingTexts.signingCancelled');
              this.signingCancelled = true;
            }
            this.loading = false;
            this.isSignSuccessfully = false;
          }
        }
      );
  }

  checkForDownloads() {
    const options: SigningHistoryRequest = {};
    options.orderByFilter = 'signedDate';
    options.order = 'DESC';
    options.relayingParty = environment.relyingPartyName;
    options.isLogin = false;
    options.status = DocumentStatus.SIGNED;
    options.type = "DOCUMENT";
    options.additionalParams = this.getAdditionalParams();
    this.loadingComponents = false;
    this.historyService.getSigningHistory(options).subscribe((res) => {
      if (res.data) {
        this.loadingComponents = false;
        // filtering downloads for not downloaded file with signed or archived status
        // contentType ids for contract and issuing request 100 and 110 are filtered out
        this.downloads = res.data.content.filter((r) => r.parcelUsageId !== -1 && r.contentTypeId !== ContentType.CQES_CONTRACT && r.contentTypeId !== ContentType.CQES_REQUEST && r.contentTypeId !== 10);

        if (this.downloads.length > 0) {
          this.downloads.forEach(d => {
            d.expiryDate = this.calculateExpiryDate(new Date(d.signedDate));
          });
          this.notDownloadedDocuments = true;
        }
      }
    });
  }

  getAdditionalParams() {
    const params = [];
    params.push(AdditionalParams.ARCHIVE_STATUS);
    params.push(AdditionalParams.TO_BE_ARCHIVED);
    return params;
  }

  calculateExpiryDate(date) {
    return date.setDate(date.getDate() + this.expirationTime);
  }

  downloadFile(file) {
    this.getFile(file.parcelUsageId);
  }

  onDownloadClick() {
    this.getFile(this._signDocReqId);
  }

  getFile(file) {
    this.signingService.getFile(file)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error('signingTexts.signingError');
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
          this.downloads = [];
          this.notDownloadedDocuments = false;
          this.checkForDownloads();
          this.store.dispatch(new ActionDownload());
        }
      );
  }

  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  onCertSelected(cert: ICertificateData) {
    this.info.dismissAll();
    this.selectedCert = cert;
    if (cert.deviceIds?.length === 0) {
      this.isCertInvalid = true;
      this.info.warning('manageTexts.noDevices');
    }
    if (cert.remainingCount === 0 && cert.packType === 'PREPAID') {
      this.isCertInvalid = true;
      this.info.warning('manageTexts.noRemainingCounts');
    } else {
      this.isCertInvalid = false;
    }
  }

  onFileChanged(event) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;

      if (file.size > environment.maxUploadSize) {
        this.file = null;
        this.info.error('signingTexts.info.fileSizeExceeded');
        this.fileForm.reset();
      } else {
        this.file = file;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {

          this.signingService.values(file.name.split('.').pop())
            .pipe(
              first(),
              takeUntil(this.unsubscribe$)
            )
            .subscribe(v => this.initTypeForm(v));
          this.fileForm.patchValue({
            file: reader.result
          });
          // need to run CD since file load runs outside of zone
          // this.cd.markForCheck();
          this.cd.detectChanges();
        };
      }
    }
  }

  private initFileForm() {
    this.fileForm = this.formBuilder.group({
      file: [null, Validators.required]
    });
  }

  private initTypeForm(values: ISigningValuesInterface) {
    this.values = values;

    this.signTypeForm = this.formBuilder.group({
      signFormat: [values.defaults.signFormat, Validators.required],
      signLevel: [values.defaults.signLevel, Validators.required],
      signType: [values.defaults.signType, Validators.required],
      signHashAlg: [values.defaults.signHashAlg, Validators.required],
      notToBeArchived: [false, Validators.required],
      visibleSignature: [true, Validators.required]
    });
  }

  private loadTranslations(): void {
    this.translateService.stream([this.i18nTexts], {})
      .subscribe((texts: any) => {
        [this.texts] = [texts[this.i18nTexts]];
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setTypes() {
    if (this.signTypeForm.value.signFormat === 'PAdES') {
      this.values.signType = this.padesTypes;
    }
    if (this.signTypeForm.value.signFormat === 'XAdES') {
      this.values.signType = this.xadesTypes;
    }
    if (this.signTypeForm.value.signFormat === 'CAdES') {
      this.values.signType = this.cadesTypes;
    }
    this.signTypeForm.controls['signType'].setValue(this.values.signType[0].value);
  }

  sign() {
    this.signingCancelled = false;
    if (this.otherSigner && this.users.length === 0) {

      this.info.error('signingTexts.selectUser');
    } else {
      this.onSubmit();
      if (this.signingStepCompleted) {
        this.stepper.selected.completed = true;
        this.stepper.next();
      }
    }
  }

  addSelf() {
    this.findUser(this.userData.personalIdWithType);
  }

  searchUser() {
    if (this.userIdentifier) {
      this.findUser(this.userIdentifier);
    }
  }

  private findUser(localUserIdentifier: string) {
    this.info.dismissAll();
    if (localUserIdentifier) {
      const identifierType = this.determineIdentifierType(localUserIdentifier);
      if (identifierType) {
        this.searchOptions = {};
        this.searchOptions[identifierType] = localUserIdentifier;
        if (identifierType === SearchUserIdentifiers.phoneNumber) {
          this.searchOptions[identifierType] = encodeURIComponent(localUserIdentifier);
        }
        this.profileService.searchUser(this.searchOptions).subscribe(res => {
          if (res.status === 404) {
            this.info.error('signingTexts.noUserFound');
          }
          if (res.status === 200 && res.data) {
            this.addSigner(res.data);
          }
        });
      } else {
        this.info.error('signingTexts.invalidIdentifier');
      }
      this.clearText();
    }
  }

  addSigner(user) {
    if (!this.users.find(u => u.cmrId === user.cmrId)) {
      this.users.push(user);
    }
  }

  removeSigner(id) {
    const index = this.users.findIndex(u => u.cmrId === id);
    if (index !== -1) {
      this.users.splice(index, 1);
    }
  }

  determineIdentifierType(userIdentifier: string) {
    let type;
    const emailPattern = new RegExp('[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$');
    const phonePattern = new RegExp('[+]{1}[0-9]+$');
    const idPattern = new RegExp('^[A-Z]+-[0-9]+$');
    if (emailPattern.test(userIdentifier)) {
      type = SearchUserIdentifiers.email;
    }
    if (phonePattern.test(userIdentifier)) {
      type = SearchUserIdentifiers.phoneNumber;
    }
    if (idPattern.test(userIdentifier)) {
      type = SearchUserIdentifiers.personalId;
    }
    return type;
  }

  clearText() {
    this.userIdentifier = '';
    this.otherSignerCertReqId = '';
  }

  displayUserName(cmrId) {
    const currentUser = this.users.find(u => u.cmrId === cmrId);
    return this.translateService.currentLang === 'bg' ? currentUser.firstName.bg + ' ' + currentUser.lastName.bg :
      currentUser.firstName.en + ' ' + currentUser.lastName.en;
  }



  displayToBeArchivedCheckbox() {
    return environment.displayToBeArchivedCheckbox;
  }

  private isSigningValid() {
    return this.fileForm.valid && this.signTypeForm.valid;
  }

  onSelectPeriod(p) {
    this.validity = p;
  }

  getUnitTranslation(p, unit) {
    return this.translateService.instant(p == 1 ? 'settingsList.' + unit : 'settingsList.' + unit + 's');
  }

}
